import { useState } from 'react';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { ImpactCounter } from '@/contexts/SiteDataContext';

export default function ImpactSection() {
  const { data, updateImpactCounters } = useSiteData();
  const [counters, setCounters] = useState<ImpactCounter[]>([...data.impactCounters]);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateImpactCounters(counters);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const update = (i: number, field: keyof ImpactCounter, val: string | number) => {
    setCounters(counters.map((c, idx) => idx === i ? { ...c, [field]: field === 'value' ? Number(val) : val } : c));
  };

  const addCounter = () => setCounters([...counters, { value: 0, suffix: '+', label: 'New Stat', icon: 'ri-star-line' }]);
  const remove = (i: number) => setCounters(counters.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground-900 mb-1">Impact Counters</h2>
          <p className="text-foreground-500 text-sm">Edit the stats shown in the "Numbers That Tell Stories" section.</p>
        </div>
        <button
          onClick={addCounter}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-xl text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i> Add Counter
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {counters.map((counter, i) => (
          <div key={i} className="border border-background-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${counter.icon} text-primary-500 text-sm`}></i>
                </div>
                <span className="text-sm font-bold text-foreground-800">{counter.label}</span>
              </div>
              <button
                onClick={() => remove(i)}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-600 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-foreground-500 mb-1">Value</label>
                <input
                  type="number"
                  value={counter.value}
                  onChange={(e) => update(i, 'value', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-500 mb-1">Suffix</label>
                <input
                  type="text"
                  value={counter.suffix}
                  onChange={(e) => update(i, 'suffix', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-primary-300"
                  placeholder="+"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-500 mb-1">Icon class</label>
                <input
                  type="text"
                  value={counter.icon}
                  onChange={(e) => update(i, 'icon', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-primary-300"
                  placeholder="ri-heart-line"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-foreground-500 mb-1">Label</label>
              <input
                type="text"
                value={counter.label}
                onChange={(e) => update(i, 'label', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
            </div>
            <p className="text-xs text-foreground-400 bg-background-100 px-3 py-1.5 rounded-lg font-mono">
              Display: <strong>{counter.value.toLocaleString()}{counter.suffix}</strong> — {counter.label}
            </p>
          </div>
        ))}
      </div>

      <button
        onClick={handleSave}
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${
          saved ? 'bg-emerald-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'
        }`}
      >
        <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
        {saved ? 'Saved!' : 'Save Impact Stats'}
      </button>
    </div>
  );
}